import { dashCase } from "./string-utils";

// Determines whether an element has a slot. If name is specified, the function will look for a corresponding named
// slot, otherwise it will look for a "default" slot (e.g. a non-empty text node or an element with no slot attribute).
export function hasSlot(el: HTMLElement, name?: string) {
  // Look for a named slot
  if (name) {
    return el.querySelector(`[slot="${name}"]`) !== null;
  }

  // Look for a default slot
  return Array.from(childNodes(el)).some((node) => {
    if (node.nodeType === node.TEXT_NODE && node.textContent.trim() !== "") {
      return true;
    }

    if (node.nodeType === node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (!el.hasAttribute("slot")) {
        return true;
      }
    }

    return false;
  });
}

export function setPropsIfNull(element: Element, attributes: any) {
  Object.keys(attributes).forEach((attribute) => {
    if (element.getAttribute(dashCase(attribute)) === null) {
      element[attribute] = attributes[attribute];
    }
  });
}

export function isStzhElement(element: Element) {
  if (!element || element.nodeType !== 1) {
    return false;
  }

  return element.tagName.toLowerCase().indexOf("stzh-") === 0;
}

// returns content of <meta name="" content=""> tags or '' if empty/non existant
export function getMeta(name) {
  const meta = document.querySelector(
    `meta[name="${name}"],[property="${name}"]`
  );
  return meta ? meta.getAttribute("content") : "";
}

export function parents(node: Element | Node) {
  const parents = [];

  while (node) {
    parents.unshift(node);
    node = node.parentNode;
  }

  return parents;
}

export function childNodes(node: Element) {
  const childNodes = node.childNodes;

  // check if element is stencil element without shadow dom
  // and then detect elements that were slotted into the element
  if (node["s-sc"]) {
    const result = [];

    for (let i = 0; i < childNodes.length; i++) {
      const slot = childNodes[i]["s-nr"];

      if (slot) {
        result.push(slot);
      }
    }

    return result;
  }

  return Array.from(childNodes);
}

export function isVisible(element: HTMLElement): boolean {
  // @ts-ignore
  if (typeof element.checkVisibility !== "undefined") {
    // @ts-ignore
    return element.checkVisibility({
      checkOpacity: false,
      checkVisibilityCSS: false,
    });
  } else {
    return !!(
      element.offsetWidth ||
      element.offsetHeight ||
      element.getClientRects().length
    );
  }
}

export function offset(element): { top: number; left: number } {
  if (!element.getClientRects().length) {
    return { top: 0, left: 0 };
  }

  let rect = element.getBoundingClientRect();
  let win = element.ownerDocument.defaultView;

  return {
    top: rect.top + win.pageYOffset,
    left: rect.left + win.pageXOffset,
  };
}

export async function raf() {
  return new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      resolve();
    });
  });
}

export async function setTimeout(time: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(() => {
      resolve();
    }, time);
  });
}

export enum StzhEnvironmentEnum {
  UNKNOWN = "UNKNOWN",
  DEV = "DEV",
  UAT = "UAT",
  PROD = "PROD",
}

export function getStzhEnvironment(): StzhEnvironmentEnum {
  const url = window.location.href;
  if (
    url.includes("test.stadt-zuerich.loc") ||
    url.match(/\.dev\.cmp\.szh\.loc/)
  ) {
    return StzhEnvironmentEnum.DEV;
  } else if (
    url.includes("integ.stadt-zuerich.ch") ||
    url.includes("stzh-desi-unic-preview.netlify.app") ||
    url.match(/\.uat\.cmp\.szh\.loc/)
  ) {
    return StzhEnvironmentEnum.UAT;
  } else if (
    url.includes("https://www.stadt-zuerich.ch") ||
    url.includes("designsystem.stadt-zuerich.ch") ||
    url.match(/\.cmp\.szh\.loc/)
  ) {
    return StzhEnvironmentEnum.PROD;
  } else {
    return StzhEnvironmentEnum.UNKNOWN;
  }
}

// `extras.experimentalSlotFixes` stencil config flag resolves cloning light dom elements
// so our own cloneElement / cloneWrappedStzhElement helpers are not needed right now
// (used before in cardlist and sortable component).

// export function children(node: Element) {
//   return childNodes(node).map((n: any) => n.nodeType === 1);
// }

// const nativeCloneNodeFn = Node.prototype.cloneNode;

// export function cloneElement(node: Element, deep: boolean) {
//   const clonedNode = nativeCloneNodeFn.call(node, false);
//   const srcChildNodes = childNodes(node);

//   if (deep) {
//     for (let i = 0; i < srcChildNodes.length; i++) {
//       if (srcChildNodes[i].nodeType !== 2) {
//         const childClone = cloneElement(srcChildNodes[i], true);
//         clonedNode.appendChild(childClone);
//       }
//     }
//   }

//   return clonedNode;
// }

// // Clone an stzh-* element that is using stencil polyfilled slots (no shadow dom)
// // and potentially wrapped in normal elements (e.g. angular component)
// export function cloneWrappedStzhElement(elementToClone: Element): [Element, Element, Element] {
//   let clonedElement: Element;
//   let clonedStzhElement: Element;
//   let originalStzhElement: Element;

//   if (isStzhElement(elementToClone)) {
//     clonedElement = cloneElement(elementToClone, true);
//     clonedStzhElement = clonedElement;
//     originalStzhElement = elementToClone;
//   } else {
//     clonedElement = elementToClone.cloneNode() as Element;

//     let clonedInnerElementLast: Element = clonedElement;
//     let originalInnerElementLast: Element = elementToClone;

//     // clone inner root elements separately until we find a stzh element
//     while (originalInnerElementLast.children.length === 1 && !isStzhElement(originalInnerElementLast.children[0])) {
//       originalInnerElementLast = originalInnerElementLast.children[0];
//       const clonedInnerElementCurrent = originalInnerElementLast.cloneNode() as Element;

//       clonedInnerElementLast.appendChild(clonedInnerElementCurrent);
//       clonedInnerElementLast = clonedInnerElementCurrent;
//     }

//     if (isStzhElement(originalInnerElementLast.children[0])) {
//       clonedStzhElement = cloneElement(originalInnerElementLast.children[0], true);
//       originalStzhElement = originalInnerElementLast.children[0];
//       clonedInnerElementLast.appendChild(clonedStzhElement);
//     } else {
//       // no root stzh element found, so we give up and just deep clone the root
//       clonedElement = cloneElement(elementToClone, true);
//     }
//   }

//   return [clonedElement, clonedStzhElement, originalStzhElement];
// }
